/**
 * Average Rating Badge Component
 * Compact rating display for tool cards
 */

import { Star } from "lucide-react";
import { cn } from "@/utils/cn";

interface AverageRatingBadgeProps {
  /** Average rating (null if no reviews) */
  averageRating: number | null | undefined;
  /** Number of ratings */
  ratingCount: number;
  /** Size variant */
  size?: "sm" | "md";
  /** Additional className */
  className?: string;
}

export function AverageRatingBadge({
  averageRating,
  ratingCount,
  size = "sm",
  className,
}: AverageRatingBadgeProps) {
  // Don't render if no ratings
  if (!ratingCount || ratingCount === 0) {
    return null;
  }

  const displayRating = averageRating?.toFixed(1) ?? "0.0";

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1 rounded-full bg-yellow-50 dark:bg-yellow-900/20",
        size === "sm" ? "px-2 py-0.5" : "px-2.5 py-1",
        className,
      )}
    >
      <Star
        className={cn(
          "fill-yellow-400 text-yellow-400",
          size === "sm" ? "w-3 h-3" : "w-4 h-4",
        )}
      />
      <span
        className={cn(
          "font-medium text-yellow-700 dark:text-yellow-300",
          size === "sm" ? "text-xs" : "text-sm",
        )}
      >
        {displayRating}
      </span>
      <span
        className={cn(
          "text-yellow-600 dark:text-yellow-400",
          size === "sm" ? "text-xs" : "text-sm",
        )}
      >
        ({ratingCount})
      </span>
    </div>
  );
}
